import React, { useState, useEffect, useRef, Fragment } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext.jsx'
import { Dialog, Transition } from '@headlessui/react'
import {
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  XMarkIcon,
  CheckIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

function formatDate(dateString) {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

// ─── Confirm Delete Modal ──────────────────────────────────────────────────
function DeleteModal({ open, onClose, onConfirm, noteTitle, loading }) {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-sm bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-2xl">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-red-950/60 border border-red-800/50 flex items-center justify-center shrink-0">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                  </div>
                  <div>
                    <Dialog.Title className="text-base font-semibold text-white">
                      Delete note?
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-400">
                      "{noteTitle}" will be permanently deleted.
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={onClose}
                    disabled={loading}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={onConfirm}
                    disabled={loading}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-700 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <TrashIcon className="h-4 w-4" />
                    )}
                    Delete
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

// ─── Edit Modal ────────────────────────────────────────────────────────────
function EditModal({ open, onClose, note, onSave, saving }) {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')

  useEffect(() => {
    if (note) {
      setTitle(note.title || '')
      setContent(note.content || '')
    }
  }, [note])

  const handleSave = () => {
    onSave({ title: title.trim(), content: content.trim() })
  }

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
                  <Dialog.Title className="text-base font-semibold text-white">Edit note</Dialog.Title>
                  <button onClick={onClose} className="text-gray-500 hover:text-gray-300 transition-colors">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                      Title
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                      placeholder="Note title"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                      Content
                    </label>
                    <textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      rows={8}
                      className="w-full bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition resize-none"
                      placeholder="Write your note…"
                    />
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-800">
                  <button
                    onClick={onClose}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !title.trim()}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {saving ? (
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <CheckIcon className="h-4 w-4" />
                    )}
                    Save
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

// ─── Main Notes page ───────────────────────────────────────────────────────
export default function Notes() {
  const { token } = useAuth()

  const [notes, setNotes] = useState([])
  const [fetching, setFetching] = useState(true)
  const [fetchError, setFetchError] = useState('')

  // Create form
  const [createTitle, setCreateTitle] = useState('')
  const [createContent, setCreateContent] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState(null) // { id, title }
  const [deleting, setDeleting] = useState(false)

  // Edit modal
  const [editTarget, setEditTarget] = useState(null)
  const [saving, setSaving] = useState(false)

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }

  // Fetch notes
  const loadNotes = async () => {
    setFetching(true)
    setFetchError('')
    try {
      const res = await fetch('/api/notes', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) throw new Error('Failed to fetch notes')
      const data = await res.json()
      setNotes(data.notes || [])
    } catch (err) {
      setFetchError(err.message)
    } finally {
      setFetching(false)
    }
  }

  useEffect(() => {
    loadNotes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Create note
  const handleCreate = async (e) => {
    e.preventDefault()
    if (!createTitle.trim()) return
    setCreating(true)
    setCreateError('')
    try {
      const res = await fetch('/api/notes', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ title: createTitle.trim(), content: createContent.trim() })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to create note')
      setNotes((prev) => [data.note, ...prev])
      setCreateTitle('')
      setCreateContent('')
      setShowCreateForm(false)
    } catch (err) {
      setCreateError(err.message)
    } finally {
      setCreating(false)
    }
  }

  // Delete note
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      const res = await fetch(`/api/notes/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) throw new Error('Failed to delete note')
      setNotes((prev) => prev.filter((n) => n.id !== deleteTarget.id))
      setDeleteTarget(null)
    } catch (err) {
      // Keep modal open on error
      console.error(err)
    } finally {
      setDeleting(false)
    }
  }

  // Save edited note
  const handleSave = async ({ title, content }) => {
    if (!editTarget) return
    setSaving(true)
    try {
      const res = await fetch(`/api/notes/${editTarget.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ title, content })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to save note')
      setNotes((prev) => prev.map((n) => (n.id === editTarget.id ? data.note : n)))
      setEditTarget(null)
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Nav */}
      <nav className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-4">
          <Link
            to="/dashboard"
            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Dashboard
          </Link>
          <span className="text-gray-700">/</span>
          <span className="text-sm font-medium text-white">Notes</span>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Page header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Notes</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              {notes.length} {notes.length === 1 ? 'note' : 'notes'}
            </p>
          </div>
          <button
            onClick={() => {
              setShowCreateForm((v) => !v)
              setCreateError('')
            }}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            New note
          </button>
        </div>

        {/* Create form */}
        {showCreateForm && (
          <form
            onSubmit={handleCreate}
            className="mb-8 bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-4"
          >
            <h2 className="text-sm font-semibold text-gray-300 mb-2">Create new note</h2>

            {createError && (
              <p className="text-sm text-red-400 bg-red-950/40 border border-red-800/40 rounded-lg px-3 py-2">
                {createError}
              </p>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                Title <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={createTitle}
                onChange={(e) => setCreateTitle(e.target.value)}
                required
                className="w-full bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="Note title"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                Content
              </label>
              <textarea
                value={createContent}
                onChange={(e) => setCreateContent(e.target.value)}
                rows={5}
                className="w-full bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition resize-none"
                placeholder="Write your note…"
              />
            </div>

            <div className="flex justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false)
                  setCreateTitle('')
                  setCreateContent('')
                  setCreateError('')
                }}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || !createTitle.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50"
              >
                {creating ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <PlusIcon className="h-4 w-4" />
                )}
                Create
              </button>
            </div>
          </form>
        )}

        {/* States */}
        {fetching ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : fetchError ? (
          <div className="text-center py-20">
            <p className="text-red-400 text-sm">{fetchError}</p>
            <button
              onClick={loadNotes}
              className="mt-3 text-sm text-indigo-400 hover:text-indigo-300 underline"
            >
              Retry
            </button>
          </div>
        ) : notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <DocumentTextIcon className="h-12 w-12 text-gray-700 mb-4" />
            <p className="text-gray-400 font-medium">No notes yet</p>
            <p className="text-sm text-gray-600 mt-1">Click "New note" to create your first one.</p>
          </div>
        ) : (
          /* Notes grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {notes.map((note) => (
              <div
                key={note.id}
                className="group bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-xl p-5 transition-all flex flex-col"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-white text-sm leading-snug line-clamp-2 flex-1">
                    {note.title}
                  </h3>
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setEditTarget(note)}
                      title="Edit"
                      className="p-1.5 text-gray-500 hover:text-indigo-400 hover:bg-indigo-500/10 rounded-lg transition-colors"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget({ id: note.id, title: note.title })}
                      title="Delete"
                      className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {note.content && (
                  <p className="text-sm text-gray-400 line-clamp-3 flex-1 mb-3">
                    {note.content}
                  </p>
                )}

                <p className="text-xs text-gray-600 mt-auto">
                  {note.updated_at !== note.created_at
                    ? `Updated ${formatDate(note.updated_at)}`
                    : `Created ${formatDate(note.created_at)}`}
                </p>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Modals */}
      <DeleteModal
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        noteTitle={deleteTarget?.title || ''}
        loading={deleting}
      />

      <EditModal
        open={!!editTarget}
        onClose={() => !saving && setEditTarget(null)}
        note={editTarget}
        onSave={handleSave}
        saving={saving}
      />
    </div>
  )
}
